import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { GlobalSearchService } from '../../services/globalsearchservice.service'
import { PrintService } from '../../services/print.service'
import { FormControl } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
	selector: 'app-trial-balance',
	templateUrl: './trial-balance.component.html',
	styleUrls: ['./trial-balance.component.scss'],
	animations: [
		trigger('detailExpand', [
			state('collapsed, void', style({ height: '0px', minHeight: '0', display: 'none' })),
			state('expanded', style({ height: '*' })),
			transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
			transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
		]),
	],
})
export class TrialBalanceComponent implements OnInit, OnChanges, AfterViewInit {
	@Input() data: any = [];
	/** From API: full `salesman` table — keeps the dropdown populated even when rows lack a rep. */
	@Input() salesmenCatalog: { code: string; name: string }[] = [];
	/** Wallet column opens account overview (AR, cards, open docs). Used from overdue / trial-balance dashboard. */
	@Input() showAccountOverviewAction = false;
	/** Payment column opens full payment entry (`app-payment`) in a modal. */
	@Input() showPaymentAction = false;
	/** Show Refresh next to Search (e.g. overdue dashboard re-fetch). */
	@Input() showToolbarRefresh = false;
	@Input() toolbarRefreshDisabled = false;
	@Output() accountOverview = new EventEmitter<{ debtorno: string; branchcode: string; title: string }>();
	@Output() takePayment = new EventEmitter<{ debtorno: string; branchcode: string; title: string }>();
	@Output() toolbarRefresh = new EventEmitter<void>();
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild('printTable') printTableRed: ElementRef;
	sumdet = 'summary';

	trial_balance_columns: any = [
		'name',
		'debtorno',
		'terms',
		'creditlimit',
		'reasondescription',
		'typename',
		'balance',
		'future',
		'current',
		'due',
		'overdue',
		'overdue1',
		'overdue2',
	];

	po_columns: string[] = [
		'typename_po',
		'transno_po',
		'order__po',
		'trandate_po',
		'daysbeforedue_po',
		'payterms_po',
		'balance_po',
		'future_po',
		'current_po',
		'due_po',
		'overdue1_po',
		'overdue2_po',
		'reasondescription_po',
	];

	trial_balance_columns_expand: any = [...this.trial_balance_columns, 'expand'];
	expandedElement: poElement | null;

	/** `true` = Summary, `false` = Detail — must default to `true` so first paint matches Summary. */
	summary_details = new FormControl<boolean>(true, { nonNullable: true });
	summary_or_details = [
		{ label: 'Summary', value: true },
		{ label: 'Detail', value: false },
	]
	show_details = false;

	matdata: any
	pdf = false;
	hold_data: any = [];
	/** After structural filters (salesman, terms, card, etc.); search overlays to `gridRows`. */
	all: any = [];
	/** Input snapshot / master rows (do not assign filtered rows to `@Input() data`). */
	masterData: any = [];
	/** Rows shown in the grid (filters + search + sort). */
	gridRows: any[] = [];
	sending = false;
	pageSize = 2500;
	total_balance = 0;
	total_current = 0;
	total_due = 0;
	total_overdue1 = 0;
	total_overdue2 = 0;
	total_overdue = 0;
	total_future = 0;
	paginator_show = true;
	arlink: any = '';

	private static readonly FILTER_STORAGE_KEY = 'arv8.sharedTrialBalance.filters';

	/** Empty = no restriction (all). Otherwise row must match one of the selected codes. */
	filterSalesmanCodes: string[] = [];
	filterTermsCodes: string[] = [];
	/** When false, rows with balance &lt; 0 are hidden. Default true = show everyone. */
	includeNegativeBalances = true;
	/** all = no restriction; yes = card_on_file Y; no = not Y */
	cardOnFileFilter: 'all' | 'yes' | 'no' = 'all';
	/** Search box — overlaid after structural filters; persisted with session filters. */
	toolbarSearchText = '';
	private summaryRestoredFromStorage = false;
	salesmanOptions: { code: string; name: string }[] = [];
	termsOptions: { code: string; label: string }[] = [];

	/** Client-side column sort (plain HTML table, not mat-table). */
	sortColumn: string | null = null;
	sortDir: 'asc' | 'desc' = 'asc';

	private readonly sortNumericKeys = new Set([
		'creditlimit',
		'balance',
		'future',
		'current',
		'due',
		'overdue',
		'overdue1',
		'overdue2',
	]);

	/** Main grid column count (detail expand row colspan). */
	get trialBalanceColCount(): number {
		return (
			15 +
			(this.showAccountOverviewAction ? 1 : 0) +
			(this.showPaymentAction ? 1 : 0)
		);
	}

	constructor(private globalSearchService: GlobalSearchService, private printService: PrintService) {}

	ngOnInit(): void {
		this.loadPersistedFilters();
		this.arlink = this.globalSearchService.config;
		if (!this.summaryRestoredFromStorage) {
			this.summary_details.setValue(true);
			this.show_details = false;
		}
		this.summary_details.valueChanges.subscribe((newvalue) => {
			if (!newvalue) {
				this.show_details = true;
			} else {
				this.show_details = false;
			}
			this.savePersistedFilters();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['data']) {
			this.initFromInputData();
		} else if (changes['salesmenCatalog'] && this.masterData.length) {
			this.buildFilterOptions();
			this.applyFilters();
		}
	}

	ngAfterViewInit(): void {
		this.attachSortAndPaginator();
	}

	private initFromInputData(): void {
		const incoming = Array.isArray(this.data) ? this.data : [];
		this.masterData = incoming.map((row) => (row && typeof row === 'object' ? { ...row } : row));
		this.hold_data = this.masterData;
		this.buildFilterOptions();
		this.pruneInvalidFilterSelections();
		this.applyFilters();
	}

	private loadPersistedFilters(): void {
		try {
			const raw = sessionStorage.getItem(TrialBalanceComponent.FILTER_STORAGE_KEY);
			if (!raw) {
				return;
			}
			const p = JSON.parse(raw) as Record<string, unknown>;
			if (Array.isArray(p['filterSalesmanCodes'])) {
				this.filterSalesmanCodes = (p['filterSalesmanCodes'] as unknown[])
					.map((c) => String(c).trim())
					.filter((c) => c !== '');
			}
			if (Array.isArray(p['filterTermsCodes'])) {
				this.filterTermsCodes = (p['filterTermsCodes'] as unknown[])
					.map((c) => String(c).trim())
					.filter((c) => c !== '');
			}
			if (typeof p['includeNegativeBalances'] === 'boolean') {
				this.includeNegativeBalances = p['includeNegativeBalances'];
			}
			const cof = p['cardOnFileFilter'];
			if (cof === 'all' || cof === 'yes' || cof === 'no') {
				this.cardOnFileFilter = cof;
			}
			if (p['sortColumn'] === null || typeof p['sortColumn'] === 'string') {
				this.sortColumn = p['sortColumn'] as string | null;
			}
			if (p['sortDir'] === 'asc' || p['sortDir'] === 'desc') {
				this.sortDir = p['sortDir'];
			}
			if (typeof p['summaryDetails'] === 'boolean') {
				this.summary_details.setValue(p['summaryDetails'], { emitEvent: false });
				this.show_details = !p['summaryDetails'];
				this.summaryRestoredFromStorage = true;
			}
			if (typeof p['toolbarSearchText'] === 'string') {
				this.toolbarSearchText = p['toolbarSearchText'];
			}
		} catch {
			/* ignore corrupt storage */
		}
	}

	private savePersistedFilters(): void {
		try {
			sessionStorage.setItem(
				TrialBalanceComponent.FILTER_STORAGE_KEY,
				JSON.stringify({
					filterSalesmanCodes: this.filterSalesmanCodes,
					filterTermsCodes: this.filterTermsCodes,
					includeNegativeBalances: this.includeNegativeBalances,
					cardOnFileFilter: this.cardOnFileFilter,
					sortColumn: this.sortColumn,
					sortDir: this.sortDir,
					summaryDetails: this.summary_details.value,
					toolbarSearchText: this.toolbarSearchText,
				}),
			);
		} catch {
			/* ignore quota / private mode */
		}
	}

	private pruneInvalidFilterSelections(): void {
		const salesCodes = new Set(this.salesmanOptions.map((s) => s.code));
		const termKeys = new Set(this.termsOptions.map((t) => t.code));
		this.filterSalesmanCodes = this.filterSalesmanCodes.filter((c) => salesCodes.has(c));
		this.filterTermsCodes = this.filterTermsCodes.filter((c) => termKeys.has(c));
	}

	private getSalesmanCode(c: any): string {
		const v = c?.salesman ?? c?.salesmancode ?? c?.salesperson ?? '';
		return String(v).trim();
	}

	/** Terms filter key: prefer API termsindicator, else fall back to display terms (always on aged rows). */
	private getTermsFilterKey(c: any): string {
		const ti = c?.termsindicator != null ? String(c.termsindicator).trim() : '';
		if (ti) {
			return ti;
		}
		return c?.terms != null ? String(c.terms).trim() : '';
	}

	private buildFilterOptions(): void {
		const salesMap = new Map<string, string>();
		const catalog = Array.isArray(this.salesmenCatalog) ? this.salesmenCatalog : [];
		for (const s of catalog) {
			const code = s?.code != null ? String(s.code).trim() : '';
			if (!code) {
				continue;
			}
			const name = s?.name != null ? String(s.name).trim() : '';
			salesMap.set(code, name || code);
		}
		for (const c of this.masterData) {
			const code = this.getSalesmanCode(c);
			if (!code) {
				continue;
			}
			const name = (c?.salesmanname != null ? String(c.salesmanname) : '').trim();
			if (!salesMap.has(code)) {
				salesMap.set(code, name || code);
			} else if (name && salesMap.get(code) === code) {
				salesMap.set(code, name);
			}
		}
		this.salesmanOptions = Array.from(salesMap.entries())
			.map(([code, name]) => ({ code, name }))
			.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

		const termsMap = new Map<string, string>();
		for (const c of this.masterData) {
			const code = this.getTermsFilterKey(c);
			if (!code) {
				continue;
			}
			const label = (c?.terms != null ? String(c.terms).trim() : '') || code;
			if (!termsMap.has(code)) {
				termsMap.set(code, label);
			}
		}
		this.termsOptions = Array.from(termsMap.entries())
			.map(([code, label]) => ({ code, label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}

	private rowMatchesFilters(customer: any): boolean {
		if (!this.includeNegativeBalances && (parseFloat(customer.balance) < 0)) {
			return false;
		}
		if (this.cardOnFileFilter !== 'all') {
			const raw = customer?.card_on_file != null ? String(customer.card_on_file).trim().toUpperCase() : '';
			const hasCard = raw === 'Y';
			if (this.cardOnFileFilter === 'yes' && !hasCard) {
				return false;
			}
			if (this.cardOnFileFilter === 'no' && hasCard) {
				return false;
			}
		}
		if (this.filterSalesmanCodes.length > 0) {
			const sc = this.getSalesmanCode(customer);
			if (!this.filterSalesmanCodes.includes(sc)) {
				return false;
			}
		}
		if (this.filterTermsCodes.length > 0) {
			const tk = this.getTermsFilterKey(customer);
			if (!this.filterTermsCodes.includes(tk)) {
				return false;
			}
		}
		return true;
	}

	applyFilters(): void {
		this.all = this.masterData.filter((c) => this.rowMatchesFilters(c));
		this.applySearchOverlay();
	}

	/** Build `gridRows` from `all` and the search box; do not write to `@Input() data` (Angular resets inputs from parent). */
	private applySearchOverlay(): void {
		const v = this.toolbarSearchText ?? '';
		if (v.trim() !== '') {
			this.gridRows = this.globalSearchService.filterItem(
				this.all,
				v,
				'name,debtorno,terms,termsindicator,salesman,salesmanname,card_on_file,typename,balance,future,current,due,overdue,overdue1,overdue2',
			);
		} else {
			this.gridRows = Array.isArray(this.all) ? this.all : [];
		}
		this.updateTotals();
		this.matdata = new MatTableDataSource(this.gridRows);
		this.attachSortAndPaginator();
		this.applySortToData();
		this.savePersistedFilters();
	}

	private attachSortAndPaginator(): void {
		if (!this.matdata) {
			return;
		}
		if (this.paginator) {
			this.matdata.paginator = this.paginator;
		}
	}

	updateTotals() {
		const rows = Array.isArray(this.gridRows) ? this.gridRows : [];
		this.total_balance = rows.reduce((acc, c) => acc + (parseFloat(c?.balance) || 0), 0);
		this.total_future = rows.reduce((acc, c) => acc + (parseFloat(c?.future) || 0), 0);
		this.total_current = rows.reduce((acc, c) => acc + (parseFloat(c?.current) || 0), 0);
		this.total_due = rows.reduce((acc, c) => acc + (parseFloat(c?.due) || 0), 0);
		this.total_overdue = rows.reduce((acc, c) => acc + (parseFloat(c?.overdue) || 0), 0);
		this.total_overdue1 = rows.reduce((acc, c) => acc + (parseFloat(c?.overdue1) || 0), 0);
		this.total_overdue2 = rows.reduce((acc, c) => acc + (parseFloat(c?.overdue2) || 0), 0);
	}

	onToolbarSearchChange(): void {
		this.applySearchOverlay();
	}

	setSort(column: string): void {
		if (!column) {
			return;
		}
		if (this.sortColumn === column) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDir = 'asc';
		}
		this.applySortToData();
		this.savePersistedFilters();
	}

	sortIndicator(column: string): 'asc' | 'desc' | null {
		return this.sortColumn === column ? this.sortDir : null;
	}

	private applySortToData(): void {
		if (!this.sortColumn || !Array.isArray(this.gridRows) || this.gridRows.length === 0) {
			return;
		}
		const col = this.sortColumn;
		const mul = this.sortDir === 'asc' ? 1 : -1;
		this.gridRows = [...this.gridRows].sort((a, b) => mul * this.compareSortRows(a, b, col));
		this.matdata = new MatTableDataSource(this.gridRows);
		this.attachSortAndPaginator();
	}

	private compareSortRows(a: any, b: any, col: string): number {
		if (col === 'salesperson') {
			const sa = this.salespersonDisplay(a).toLowerCase();
			const sb = this.salespersonDisplay(b).toLowerCase();
			return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
		}
		if (this.sortNumericKeys.has(col)) {
			const na = parseFloat(a?.[col]) || 0;
			const nb = parseFloat(b?.[col]) || 0;
			if (na === nb) {
				return 0;
			}
			return na < nb ? -1 : 1;
		}
		let sa = a?.[col];
		let sb = b?.[col];
		sa = sa != null ? String(sa).trim().toLowerCase() : '';
		sb = sb != null ? String(sb).trim().toLowerCase() : '';
		return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
	}

	exportPdf() {
		this.sending = true;
		this.paginator_show = false;
		const today = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.printTableRed.nativeElement.innerHTML);
		const data = {
			content: encoded,
			filename: 'ar_balance',
			landscape: true,
			title: 'Customer Trial Balance',
			subtitle: today.toDateString(),
		}

		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.sending = false;
		});
	}

	exportXls() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printTableRed.nativeElement.innerHTML);

		const today = new Date();

		const data = {
			content: encoded,
			filename: 'ar_balance',
			title: 'Customer Trial Balance',
			subtitle: today.toDateString(),
		}

		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.sending = false;
		});
	}

	emitAccountOverview(row: any, ev?: Event): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}
		const payload = this.customerActionPayload(row);
		if (!payload) {
			return;
		}
		this.accountOverview.emit(payload);
	}

	emitTakePayment(row: any, ev?: Event): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}
		const payload = this.customerActionPayload(row);
		if (!payload) {
			return;
		}
		this.takePayment.emit(payload);
	}

	private customerActionPayload(row: any): { debtorno: string; branchcode: string; title: string } | null {
		const debtorno = row?.debtorno != null ? String(row.debtorno).trim() : '';
		const branchcode = this.resolveBranchcodeForRow(row);
		if (!debtorno || !branchcode) {
			return null;
		}
		const name = row?.name != null ? String(row.name).trim() : '';
		const title = name ? `${name} · ${debtorno}.${branchcode}` : `${debtorno}.${branchcode}`;
		return { debtorno, branchcode, title };
	}

	emitToolbarRefresh(): void {
		this.toolbarRefresh.emit();
	}

	/** Main grid: salesman label (API `salesman` / `salesmanname`). */
	salespersonDisplay(row: any): string {
		const name = row?.salesmanname != null ? String(row.salesmanname).trim() : '';
		const code = row?.salesman != null ? String(row.salesman).trim() : '';
		if (name && code) {
			return `${name} (${code})`;
		}
		if (name) {
			return name;
		}
		if (code) {
			return code;
		}
		return '—';
	}

	private resolveBranchcodeForRow(row: any): string {
		const direct = row?.branchcode != null ? String(row.branchcode).trim() : '';
		if (direct) {
			return direct;
		}
		const details = row?.details;
		if (!Array.isArray(details)) {
			return '';
		}
		for (const d of details) {
			const bc = d?.branchcode != null ? String(d.branchcode).trim() : '';
			if (bc) {
				return bc;
			}
		}
		return '';
	}

}

export interface poElement {
	details: Array < any > ;
}
